import React, { useState, useEffect } from 'react';
import 'assets/styles/Home/HomeUI/zonal-statistics-window.css';
import useActiveLayersStore from 'stores/useSelectedLayersStore';

const ZonalStatisticsToolWindow = ({ ZSTPopUpContent, setZSTPopUpContent, startDate, layerName }) => {
  const [currentLayerIndex, setCurrentLayerIndex] = useState(0);
  const [currentStats, setCurrentStats] = useState(null);
  const [layerNames, setLayerNames] = useState([]);
  

  const selectedLayers = useActiveLayersStore((state) => state.selectedLayers);
  const [displayName, setDisplayName] = useState(selectedLayers[selectedLayers.length - 1]);
  const isLoading = ZSTPopUpContent?.isLoading;

  useEffect(() => {
    if (ZSTPopUpContent && ZSTPopUpContent.allData) {
      // multiple layers
      const names = Object.keys(ZSTPopUpContent.allData);
      setLayerNames(names);

      if (names.length > 0) {
        const currentName = names[currentLayerIndex];
        const layerData = ZSTPopUpContent.allData[currentName];

        if (layerData && layerData.length > 0) {
          setCurrentStats(processLayerStats(layerData[0]));
          setDisplayName(formatLayerName(currentName));
        }
      }
    } else if (ZSTPopUpContent && !ZSTPopUpContent.isLoading) {
      // single layer
      setCurrentStats(ZSTPopUpContent);
      setLayerNames([layerName]);
      setDisplayName(formatLayerName(layerName));
    }
  }, [ZSTPopUpContent, currentLayerIndex, layerName]);

  // this is to format layers instead of vegetation_condition it will be Vegetation Condition and so on
  const formatLayerName = (name) => {
    if (!name) return '';

    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // extract stats
  const processLayerStats = (layerData) => {
    if (!layerData) return null;

    // If the data is already in the expected format, return it
    if (layerData.min !== undefined && layerData.max !== undefined) {
      return layerData;
    }

    // try to extract statistics from the structure
    const stats = {};

    if (Array.isArray(layerData)) {
      // Case when each statistic is an object
      layerData.forEach(stat => {
        if (stat.statistic && stat.value !== undefined) {
          const statName = stat.statistic.toLowerCase();
          if (statName.includes('min')) stats.min = stat.value;
          else if (statName.includes('max')) stats.max = stat.value;
          else if (statName.includes('mean')) stats.mean = stat.value;
          else if (statName.includes('median')) stats.median = stat.value;
          else if (statName.includes('sum')) stats.sum = stat.value;
          else if (statName.includes('std') || statName.includes('dev')) stats.std = stat.value;
        }
      });
    } else {
      // Case when each statistic is a property
      if (layerData.min !== undefined) stats.min = layerData.min;
      if (layerData.max !== undefined) stats.max = layerData.max;
      if (layerData.mean !== undefined) stats.mean = layerData.mean;
      if (layerData.median !== undefined) stats.median = layerData.median;
      if (layerData.sum !== undefined) stats.sum = layerData.sum;
      if (layerData.std !== undefined) stats.std = layerData.std;
    }

    return stats;
  };

  const handleClose = () => {
    setZSTPopUpContent(null);
  };

  const navigateToPrevLayer = () => {
    if (layerNames.length > 1) {
      const newIndex = (currentLayerIndex - 1 + layerNames.length) % layerNames.length;
      setCurrentLayerIndex(newIndex);

      const newLayerName = layerNames[newIndex];
      setDisplayName(formatLayerName(newLayerName));
    }
  };

  const navigateToNextLayer = () => {
    if (layerNames.length > 1) {
      const newIndex = (currentLayerIndex + 1) % layerNames.length;
      setCurrentLayerIndex(newIndex);

      const newLayerName = layerNames[newIndex];
      setDisplayName(formatLayerName(newLayerName));
    }
  };

  if (isLoading) {
    return (
      <div className="zst-window">
        <button className="close-button" onClick={handleClose}>
          &times;
        </button>
        <div className="content">
          <div className="header">
            <p className="date">{startDate.format('DD/MM/YYYY')}</p>
            <div className="layer-name">Loading Statistics...</div>
          </div>
          <div className="stats-loading">
            <div className="spinner"></div>
            <p>Fetching zonal statistics data...</p>
            <p className="loading-info">This may take a moment, especially for multiple layers</p>
            {ZSTPopUpContent?.progressStatus && (
              <p className="progress-status">{ZSTPopUpContent.progressStatus}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!ZSTPopUpContent || !currentStats) {
    return null;
  }

  return (
    <div className="zst-window">
      <button className="close-button" onClick={handleClose}>
        &times;
      </button>
      <div className="content">
        <div className="header">
          <p className="date">{startDate.format('DD/MM/YYYY')}</p>
          <div className="layer-name">{displayName}
            {layerNames.length > 1 && (
              <span className="layer-navigation">
                <button
                  className="nav-button"
                  onClick={navigateToPrevLayer}
                  title="Previous layer"
                >
                  <i className="fa-solid fa-caret-left"></i>
                </button>
                <span className="layer-indicator">
                  {currentLayerIndex + 1} / {layerNames.length}
                </span>
                <button
                  className="nav-button"
                  onClick={navigateToNextLayer}
                  title="Next layer"
                >
                  <i className="fa-solid fa-caret-right"></i>
                </button>
              </span>
            )}
          </div>
        </div>

        <div className="stats">
          <div className="stat-column">
            <p><strong>Min:</strong> {currentStats?.min ? currentStats.min.toFixed(3).replace('.', ',') : '-'}</p>
            <p><strong>Max:</strong> {currentStats?.max ? currentStats.max.toFixed(3).replace('.', ',') : '-'}</p>
            <p><strong>Median:</strong> {currentStats?.median ? currentStats.median.toFixed(3).replace('.', ',') : '-'}</p>
          </div>
          <div className="stat-column">
            <p><strong>Mean:</strong> {currentStats?.mean ? currentStats.mean.toFixed(3).replace('.', ',') : '-'}</p>
            <p><strong>Sum:</strong> {currentStats?.sum ? currentStats.sum.toFixed(3).replace('.', ',') : '-'}</p>
            <p><strong>St. dev.:</strong> {currentStats?.std ? currentStats.std.toFixed(3).replace('.', ',') : '-'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZonalStatisticsToolWindow;